import type { TenantAdminCreateValues } from '@/features/tenant/tenant-admin-management/schema'
import type { TenantAdminCreateRequest } from '@/shared/api/types/user-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCreateTenantAdminMutation } from '@/entities/tenant-admin'
import { PasswordRequirements } from '@/features/tenant/tenant-admin-management/password-requirements'
import { tenantAdminCreateSchema } from '@/features/tenant/tenant-admin-management/schema'
import { HTTP_STATUS } from '@/shared/constants/http'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'

const FORM_ID = 'tenant-admin-create-form'
const PASSWORD_RULES_ID = 'tenant-admin-password-rules'

const EMPTY_VALUES: TenantAdminCreateValues = {
  fullName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
}

interface TenantAdminCreateDialogProps {
  open: boolean
  tenantId: string
  tenantName?: string | undefined
  onOpenChange: (open: boolean) => void
}

export function TenantAdminCreateDialog({
  open,
  tenantId,
  tenantName,
  onOpenChange,
}: TenantAdminCreateDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('tenant.admin.create')}</DialogTitle>
          <DialogDescription>
            {tenantName
              ? t('tenant.admin.createDescription', { tenant: tenantName })
              : t('tenant.admin.createDescriptionGeneric')}
          </DialogDescription>
        </DialogHeader>
        {/* Radix unmounts the content when closed, so the form (and its state) starts fresh on every open. */}
        <TenantAdminCreateForm tenantId={tenantId} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

interface TenantAdminCreateFormProps {
  tenantId: string
  onClose: () => void
}

function TenantAdminCreateForm({ tenantId, onClose }: TenantAdminCreateFormProps) {
  const { t } = useTranslation()
  const createMutation = useCreateTenantAdminMutation(tenantId)
  const [showPassword, setShowPassword] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<TenantAdminCreateValues>({
    resolver: zodResolver(tenantAdminCreateSchema),
    mode: 'onChange',
    defaultValues: EMPTY_VALUES,
  })
  const { errors, isSubmitted } = form.formState
  const password = useWatch({ control: form.control, name: 'password' })

  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  async function onSubmit(values: TenantAdminCreateValues) {
    setSubmitError(null)
    const payload: TenantAdminCreateRequest = {
      username: values.username,
      password: values.password,
      ...(values.fullName ? { fullName: values.fullName } : {}),
      ...(values.email ? { email: values.email } : {}),
    }

    try {
      const created = await createMutation.mutateAsync(payload)
      showToast.success(t('tenant.admin.createSuccess', { username: created.username }))
      onClose()
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === HTTP_STATUS.CONFLICT) {
        // Username is unique per tenant: keep the user on the field they have to change.
        form.setError('username', {
          type: 'server',
          message: 'tenant.admin.validation.usernameTaken',
        })
        form.setFocus('username')
        return
      }
      setSubmitError(
        status === HTTP_STATUS.FORBIDDEN
          ? t('tenant.admin.errors.forbidden')
          : (getApiErrorText(error) ?? t('common.operationFailed')),
      )
    }
  }

  const isPending = createMutation.isPending
  const passwordInputType = showPassword ? 'text' : 'password'
  const passwordRulesUnmet = isSubmitted && Boolean(errors.password)

  return (
    <>
      <form
        id={FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        autoComplete="off"
        noValidate
        className="space-y-4"
      >
        {submitError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {submitError}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            id="tenant-admin-full-name"
            label={t('user.fullName')}
            optional
            optionalLabel={t('common.optional')}
            error={getErrorText(errors.fullName?.message)}
          >
            <Input id="tenant-admin-full-name" autoComplete="off" {...form.register('fullName')} />
          </FormField>

          <FormField
            id="tenant-admin-email"
            label={t('user.email')}
            optional
            optionalLabel={t('common.optional')}
            error={getErrorText(errors.email?.message)}
          >
            <Input
              id="tenant-admin-email"
              inputMode="email"
              autoComplete="off"
              aria-invalid={Boolean(errors.email)}
              {...form.register('email')}
            />
          </FormField>
        </div>

        <FormField
          id="tenant-admin-username"
          label={t('user.username')}
          required
          hint={t('tenant.admin.usernameHint')}
          error={getErrorText(errors.username?.message)}
        >
          <Input
            id="tenant-admin-username"
            autoComplete="off"
            aria-invalid={Boolean(errors.username)}
            {...form.register('username')}
          />
        </FormField>

        <FormField id="tenant-admin-password" label={t('user.password')} required>
          <div className="relative">
            <Input
              id="tenant-admin-password"
              type={passwordInputType}
              autoComplete="new-password"
              className="pr-10"
              aria-invalid={passwordRulesUnmet}
              aria-describedby={PASSWORD_RULES_ID}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-zinc-500 transition-colors hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <PasswordRequirements
            id={PASSWORD_RULES_ID}
            value={password}
            highlightUnmet={passwordRulesUnmet}
          />
        </FormField>

        <FormField
          id="tenant-admin-confirm-password"
          label={t('auth.confirmPassword')}
          required
          error={getErrorText(errors.confirmPassword?.message)}
        >
          <Input
            id="tenant-admin-confirm-password"
            type={passwordInputType}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.confirmPassword)}
            {...form.register('confirmPassword')}
          />
        </FormField>
      </form>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button form={FORM_ID} type="submit" disabled={isPending} aria-busy={isPending}>
          {isPending && <Loader2 className="animate-spin" />}
          {isPending ? t('common.creating') : t('tenant.admin.create')}
        </Button>
      </DialogFooter>
    </>
  )
}
